import { getCallAudioContext } from "./KaiVoiceTones";

/**
 * Hold music for a Kai Voice call while Kai Resolve works on the request:
 * Pachelbel's Canon in D (public domain) as a soft electric-piano loop,
 * synthesised with Web Audio like the call tones, so there is no file to load
 * and no licence to track. One intro cycle (bass and broken chords), then the
 * three melody cycles loop. Stopping and starting again picks up at the next
 * beat instead of replaying the intro.
 */
const BEAT_SEC = 60 / 70;
const STEP_SEC = BEAT_SEC / 4;
const STEPS_PER_CYCLE = 32;
const LOOKAHEAD_SEC = 0.35;
// Background tabs may run the scheduling timer only once a second.
const HIDDEN_LOOKAHEAD_SEC = 1.2;
const SCHEDULE_EVERY_MS = 100;
const FADE_IN_SEC = 0.8;
const FADE_OUT_SEC = 0.3;
const MASTER_GAIN = 1.4;

// The Canon's ground bass and its chords, one per beat.
const BASS = [50, 45, 47, 42, 43, 38, 43, 45];
const CHORDS = [
  [57, 62, 66],
  [57, 61, 64],
  [59, 62, 66],
  [57, 61, 66],
  [59, 62, 67],
  [57, 62, 66],
  [59, 62, 67],
  [57, 61, 64],
];
const ARPEGGIO = [0, 1, 2, 1];
const MELODY_QUARTERS = [
  [78, 76, 74, 73, 71, 69, 71, 73],
  [74, 73, 71, 69, 67, 66, 67, 64],
];
const MELODY_EIGHTHS = [
  [78, 76],
  [76, 74],
  [74, 73],
  [73, 71],
  [71, 69],
  [69, 67],
  [71, 69],
  [73, 74],
];

const EPIANO: Array<[number, number]> = [
  [1, 1],
  [2, 0.25],
  [3, 0.08],
];
const WARM: Array<[number, number]> = [
  [1, 1],
  [2, 0.18],
];

interface Playback {
  ctx: AudioContext;
  master: GainNode;
  filter: BiquadFilterNode;
  timer?: ReturnType<typeof setInterval>;
  nextStepAt: number;
}

let playback: Playback | null = null;
let step = 0;

const midiToHz = (midi: number) => 440 * Math.pow(2, (midi - 69) / 12);

const playNote = (
  pb: Playback,
  midi: number,
  at: number,
  durationSec: number,
  gain: number,
  partials: Array<[number, number]>,
) => {
  const frequency = midiToHz(midi);
  for (const [ratio, level] of partials) {
    const osc = pb.ctx.createOscillator();
    const env = pb.ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency * ratio;
    env.gain.setValueAtTime(0.0001, at);
    env.gain.exponentialRampToValueAtTime(gain * level, at + 0.012);
    env.gain.exponentialRampToValueAtTime(0.0001, at + durationSec);
    osc.connect(env);
    env.connect(pb.master);
    osc.onended = () => env.disconnect();
    osc.start(at);
    osc.stop(at + durationSec + 0.05);
  }
};

const scheduleStep = (pb: Playback, stepIndex: number, at: number) => {
  const cycle = Math.floor(stepIndex / STEPS_PER_CYCLE);
  // Cycle 0 is the intro; afterwards the three melody cycles repeat.
  const part = cycle === 0 ? 0 : 1 + ((cycle - 1) % 3);
  const beat = Math.floor((stepIndex % STEPS_PER_CYCLE) / 4);
  const sub = stepIndex % 4;

  playNote(pb, CHORDS[beat][ARPEGGIO[sub]], at, 0.55, 0.018, WARM);
  if (sub === 0) {
    playNote(pb, BASS[beat], at, 1.6, 0.05, WARM);
    playNote(pb, BASS[beat] + 12, at, 1.2, 0.018, WARM);
    if (part === 1 || part === 2) playNote(pb, MELODY_QUARTERS[part - 1][beat], at, 1.1, 0.034, EPIANO);
  }
  if (part === 3 && (sub === 0 || sub === 2)) {
    playNote(pb, MELODY_EIGHTHS[beat][sub / 2], at, 0.7, 0.03, EPIANO);
  }
};

const pump = (pb: Playback) => {
  // A throttled timer skips the missed steps instead of playing them all at once.
  if (pb.nextStepAt < pb.ctx.currentTime) pb.nextStepAt = pb.ctx.currentTime + 0.05;
  const lookahead = typeof document !== "undefined" && document.hidden ? HIDDEN_LOOKAHEAD_SEC : LOOKAHEAD_SEC;
  while (pb.nextStepAt < pb.ctx.currentTime + lookahead) {
    scheduleStep(pb, step, pb.nextStepAt);
    step += 1;
    pb.nextStepAt += STEP_SEC;
  }
};

export const startHoldMusic = (): void => {
  if (playback) return;
  const ctx = getCallAudioContext();
  if (!ctx) return;
  try {
    const master = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    filter.type = "lowpass";
    filter.frequency.value = 3200;
    filter.Q.value = 0.7;
    master.gain.setValueAtTime(0.0001, ctx.currentTime);
    master.gain.exponentialRampToValueAtTime(MASTER_GAIN, ctx.currentTime + FADE_IN_SEC);
    master.connect(filter);
    filter.connect(ctx.destination);
    // Resume on a beat so a pause for Kai's voice does not cut a bar in half.
    step = Math.ceil(step / 4) * 4;
    const pb: Playback = { ctx, master, filter, nextStepAt: ctx.currentTime + 0.05 };
    pump(pb);
    pb.timer = setInterval(() => pump(pb), SCHEDULE_EVERY_MS);
    playback = pb;
  } catch (_) {
    // Hold music is cosmetic — the call works without it.
    playback = null;
  }
};

export const stopHoldMusic = (): void => {
  const pb = playback;
  if (!pb) return;
  playback = null;
  clearInterval(pb.timer);
  try {
    const now = pb.ctx.currentTime;
    pb.master.gain.cancelScheduledValues(now);
    pb.master.gain.setValueAtTime(Math.max(pb.master.gain.value, 0.0001), now);
    pb.master.gain.exponentialRampToValueAtTime(0.0001, now + FADE_OUT_SEC);
  } catch (_) {}
  setTimeout(
    () => {
      try {
        pb.master.disconnect();
        pb.filter.disconnect();
      } catch (_) {}
    },
    (FADE_OUT_SEC + 0.1) * 1000,
  );
};

/** A new call starts the Canon from its intro again. */
export const resetHoldMusic = (): void => {
  stopHoldMusic();
  step = 0;
};
